import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out past appointments based on SAST (UTC+2)
      const now = new Date();
      const sastOffset = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
      const nowSAST = new Date(now.getTime() + now.getTimezoneOffset() * 60 * 1000 + sastOffset);
      const todaySAST = nowSAST.toISOString().split('T')[0];
      const currentTimeSAST = nowSAST.toTimeString().slice(0, 5);
      
      const futureAppointments = response.data.filter(apt => {
        // Keep cancelled appointments for reference
        if (apt.status === 'cancelled') return false;
        
        // Filter out past appointments
        if (apt.date < todaySAST) return false;
        if (apt.date === todaySAST && apt.time < currentTimeSAST) return false;
        
        return true;
      });
      
      setAppointments(futureAppointments);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment cancelled');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <AdminLayout title="Upcoming Appointments">
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading appointments...</div>
        ) : appointments.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No upcoming appointments</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {appointments.map((appointment) => (
                  <tr key={appointment.id} className="hover:bg-slate-50" data-testid={`appointment-row-${appointment.id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-800">{appointment.patient_name}</p>
                        <p className="text-sm text-slate-500">{appointment.patient_email}</p>
                        <p className="text-sm text-slate-500">{appointment.patient_phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{appointment.date}</td>
                    <td className="px-6 py-4 text-slate-600">{appointment.time}</td>
                    <td className="px-6 py-4 text-slate-600">
                      {appointment.appointment_type === 'new_patient' ? 'New Patient' : 'Follow Up'}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(appointment.status)}</td>
                    <td className="px-6 py-4">
                      {appointment.status !== 'cancelled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelAppointment(appointment.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`cancel-appointment-${appointment.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
